import React, { useState, useEffect } from "react";

const FIELD_STYLE = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.9rem",
    padding: "0.7rem 0.9rem",
    borderRadius: 8,
    border: "1px solid #2a2a3a",
    background: "#0d0d14",
    color: "#e0e0e0",
    outline: "none",
};

const KIND_COPY = {
    waitlist: {
        heading: "Join the waitlist",
        body: "Tell us a little about you. We will email when access opens up, and your details stay with AIHumanity.",
        submitLabel: "Join waitlist",
        successBody: "Thanks. We saved your spot and will email you when access opens up.",
    },
    contact: {
        heading: "Talk to us",
        body: "Send a few details and we will follow up to schedule a conversation. Typical reply: within 2 business days.",
        submitLabel: "Send",
        successBody: "Thanks. We received your note and will follow up soon.",
    },
};

export default function LeadCaptureModal({ open, onClose, kind = "waitlist", context = "", successMessage }) {
    const copy = KIND_COPY[kind] || KIND_COPY.waitlist;
    const successBody = successMessage || copy.successBody;
    const [formData, setFormData] = useState({ name: "", email: "", company: "", useCase: context });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // null | "success" | "error"

    useEffect(() => {
        if (open) {
            setFormData({ name: "", email: "", company: "", useCase: context });
            setStatus(null);
        }
    }, [open, context]);

    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape") onClose?.();
        }
        if (open) document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleChange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await fetch("https://aihapi.onrender.com/signupbeta", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            setStatus(response.ok ? "success" : "error");
        } catch (err) {
            setStatus("error");
        }
        setLoading(false);
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.65)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 500,
                padding: 20,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: 420,
                    background: "linear-gradient(145deg, #0d0d14, #11111e)",
                    border: "1px solid #1e1e2e",
                    borderRadius: 16,
                    padding: "2rem",
                    position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        background: "none",
                        border: "none",
                        color: "#888",
                        fontSize: "1.4rem",
                        cursor: "pointer",
                        lineHeight: 1,
                    }}
                >
                    &times;
                </button>

                <h3 style={{ fontFamily: "Manrope, sans-serif", fontSize: "1.3rem", fontWeight: 700, color: "#fff", marginBottom: "0.5rem" }}>
                    {copy.heading}
                </h3>

                {status === "success" ? (
                    <p style={{ fontFamily: "Manrope, sans-serif", color: "#a0a0b8", fontSize: "0.95rem", lineHeight: 1.6 }}>
                        {successBody}
                    </p>
                ) : (
                    <>
                        <p style={{ fontFamily: "Manrope, sans-serif", color: "#888", fontSize: "0.9rem", marginBottom: "1.5rem", lineHeight: 1.6 }}>
                            {copy.body}
                        </p>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <input style={FIELD_STYLE} name="name" placeholder="Full name*" value={formData.name} onChange={handleChange} required />
                            <input style={FIELD_STYLE} type="email" name="email" placeholder="Email address*" value={formData.email} onChange={handleChange} required />
                            <input style={FIELD_STYLE} name="company" placeholder="Company (optional)" value={formData.company} onChange={handleChange} />
                            <textarea
                                style={{ ...FIELD_STYLE, resize: "vertical", minHeight: 70, fontFamily: "Manrope, sans-serif" }}
                                name="useCase"
                                placeholder="What are you hoping to use this for?"
                                value={formData.useCase}
                                onChange={handleChange}
                                required
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    fontFamily: "Manrope, sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    padding: "0.8rem",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
                                    color: "#fff",
                                    cursor: "pointer",
                                    marginTop: 4,
                                }}
                            >
                                {loading ? "Sending..." : copy.submitLabel}
                            </button>
                            {status === "error" && (
                                <p style={{ color: "#ff8fb1", fontSize: "0.85rem", fontFamily: "Manrope, sans-serif" }}>
                                    Something went wrong — please try again or email david@aihumanity.io directly.
                                </p>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
